import { Array, Effect, Option, Result, Schema } from 'effect';

import { Cart } from '@vs-shop/engine/capability';
import { capabilityLayer } from '@vs-shop/engine/capability/capabilityLayer';
import type {
  AddCartItem,
  ApplyCartCoupon,
  RemoveCartCoupon,
  RemoveCartItem,
  SetCartItemQuantity,
} from '@vs-shop/engine/capability/cart/Cart';
import { Cart as CartSchema } from '@vs-shop/engine/capability/cart/cartSchema';
import { CartError } from '@vs-shop/engine/capability/cart/errors';
import { confirmAdapterWrite } from '@vs-shop/engine/capability/cart/confirmAdapterWrite';
import { toImageTemplate } from '@vs-shop/engine/image/ImageTemplate';

import {
  addProductVariantToOrder,
  createOrder,
  deleteProductVariantFromOrder,
  editProductVariantQuantityInOrder,
  getOrderExpanded,
  updateOrder,
} from '../../bind/orderRequests';
import type { OrderDetail } from '../../schema/openapi/backend';
import { promoFreeShipping } from '../../../config/order/promo';

const decodeCart = Schema.decodeUnknownResult(Schema.toType(CartSchema));
const decodeToken = Schema.decodeUnknownEffect(Schema.String);
const decodeItemId = Schema.decodeUnknownResult(
  Schema.Finite.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1))
);

type CartSource = Pick<
  OrderDetail,
  'currencyCode' | 'items' | 'promotionCoupon' | 'promotionRuleHints'
> & {
  tokenValue?: string | null;
  itemsTotal?: number;
  total?: number;
  frontAllItemsPriceWithVatAfterDiscount?: number | null;
  frontAllItemsPriceWithoutVatAfterDiscount?: number | null;
  orderPromotionTotal?: number | null;
};

/** The rcprofi order in the engine's Cart vocabulary. */
export const toCartResult = (order: CartSource) =>
  Result.flatMap(
    Result.all(
      order.items.map((item) =>
        Result.map(decodeItemId(item.id), (id) => ({
          id: String(id),
          variantCode: item.variant.code,
          name: Option.fromNullishOr(item.productName),
          href: Option.fromNullishOr(item.variant.product.routerUrl),
          image: Option.flatMap(Option.fromNullishOr(item.variant.product.mainImage), (image) =>
            toImageTemplate(image.pathAbstract)
          ),
          quantity: item.quantity,
          unitPrice: Option.fromNullishOr(item.frontUnitPriceWithVatAfterDiscount).pipe(
            Option.orElse(() => Option.fromNullishOr(item.frontUnitPriceWithoutVatAfterDiscount)),
            Option.getOrElse(() => item.unitPrice)
          ),
          total: Option.fromNullishOr(item.frontItemsPriceWithVatAfterDiscount).pipe(
            Option.orElse(() => Option.fromNullishOr(item.frontItemsPriceWithoutVatAfterDiscount)),
            Option.getOrElse(() => item.total)
          ),
          totalWithTaxAfterDiscount: Option.fromNullishOr(item.frontItemsPriceWithVatAfterDiscount),
          unitPriceWithoutTax: Option.fromNullishOr(item.variant.priceWithoutTax),
          unitPriceBeforeDiscount: Option.fromNullishOr(item.frontUnitPriceWithVat),
          totalBeforeDiscount: Option.fromNullishOr(item.frontItemsPriceWithVat),
          maxOrderable: Option.fromNullishOr(item.variant.maxOrderable),
          ageRestriction: Option.some(item.variant.product.ageRestriction),
        }))
      )
    ),
    (items) => {
      const coupon = Option.map(Option.fromNullishOr(order.promotionCoupon), (promotion) => ({
        code: promotion.code,
        name: Option.fromNullishOr(promotion.promotionLabel),
      }));
      const freeShipping = Array.findFirst(
        order.promotionRuleHints,
        (hint) => hint.code === promoFreeShipping
      ).pipe(
        Option.flatMap((hint) => Option.fromNullishOr(hint.shippingDiscountRuleHint)),
        Option.map((rule) => ({
          threshold: rule.itemThresholdTotal,
          remaining: rule.missingItemTotal,
        }))
      );

      return decodeCart({
        token: order.tokenValue,
        currency: order.currencyCode,
        items,
        itemsTotal: order.itemsTotal ?? 0,
        total: order.total ?? 0,
        itemsTotalWithTaxAfterDiscount: Option.fromNullishOr(
          order.frontAllItemsPriceWithVatAfterDiscount
        ),
        itemsTotalWithoutTaxAfterDiscount: Option.fromNullishOr(
          order.frontAllItemsPriceWithoutVatAfterDiscount
        ),
        coupon,
        // Sylius states the promotion total as a negative adjustment.
        discount: Math.abs(
          Option.getOrElse(Option.fromNullishOr(order.orderPromotionTotal), () => 0)
        ),
        freeShipping,
      });
    }
  );

export const toCart = (order: CartSource) => Effect.fromResult(toCartResult(order));

const readCart = (token: string) => getOrderExpanded(token).pipe(Effect.flatMap(toCart));

const cart = (token: string) =>
  readCart(token).pipe(Effect.mapError(CartError.fromRequestFailure('cart', token)));

const create = confirmAdapterWrite(
  'create',
  createOrder.pipe(
    Effect.mapError(CartError.fromWriteFailure('create')),
    Effect.flatMap((written) =>
      decodeToken(written.tokenValue).pipe(Effect.mapError(CartError.fromWriteFailure('create')))
    )
  ),
  readCart
);

const addItem = (input: AddCartItem) =>
  confirmAdapterWrite(
    'addItem',
    addProductVariantToOrder(input.token, {
      productVariant: input.variantCode,
      quantity: input.quantity,
    }).pipe(
      Effect.mapError(CartError.fromWriteFailure('addItem', input.token)),
      Effect.as(input.token)
    ),
    readCart
  );

const itemId = (operation: string, token: string, value: string) =>
  Effect.gen(function* () {
    const parsed = Number(value);
    if (/^[1-9]\d*$/.test(value) && Number.isSafeInteger(parsed)) return parsed;
    return yield* new CartError.NotFound({
      operation,
      reference: Option.some(value),
      cause: new Error(`Cart item ${value} does not exist in cart ${token}`),
    });
  });

const setItemQuantity = (input: SetCartItemQuantity) =>
  confirmAdapterWrite(
    'setItemQuantity',
    itemId('setItemQuantity', input.token, input.itemId).pipe(
      Effect.flatMap((id) =>
        editProductVariantQuantityInOrder(input.token, id, input.quantity).pipe(
          Effect.mapError(CartError.fromWriteFailure('setItemQuantity', input.token))
        )
      ),
      Effect.as(input.token)
    ),
    readCart
  );

const removeItem = (input: RemoveCartItem) =>
  confirmAdapterWrite(
    'removeItem',
    itemId('removeItem', input.token, input.itemId).pipe(
      Effect.flatMap((id) =>
        deleteProductVariantFromOrder(input.token, id).pipe(
          Effect.mapError(CartError.fromWriteFailure('removeItem', input.token))
        )
      ),
      Effect.as(input.token)
    ),
    readCart
  );

/** RCProfi's PUT preserves existing email and addresses when these fields are null. */
const couponWrite = (operation: string, token: string, couponCode: Option.Option<string>) =>
  confirmAdapterWrite(
    operation,
    updateOrder(token, {
      couponCode: Option.getOrNull(couponCode),
      email: Option.getOrNull(Option.none()),
      billingAddress: Option.getOrNull(Option.none()),
      shippingAddress: Option.getOrNull(Option.none()),
    }).pipe(Effect.mapError(CartError.fromWriteFailure(operation, token)), Effect.as(token)),
    readCart
  );

const applyCoupon = (input: ApplyCartCoupon) =>
  couponWrite('applyCoupon', input.token, Option.some(input.code));

const removeCoupon = (input: RemoveCartCoupon) =>
  couponWrite('removeCoupon', input.token, Option.none());

export const CartLive = capabilityLayer(Cart, {
  cart,
  create,
  addItem,
  setItemQuantity,
  removeItem,
  applyCoupon,
  removeCoupon,
});
